/**
 * 资产中心（S06）：环境 / 本体 / 任务 / 策略 四类资产的统一管理页。
 *
 * 结构：
 *   - 类型切换（Segmented）+ 服务端分页表格（搜索 q / 状态筛选 / 排序）
 *   - 创建弹窗：四类各自的必填字段（其余字段走详情页或后续 Step）
 *   - 详情抽屉：左元信息 + 右引用关系（被哪些实验引用，S06 只出计数与最近 5 条）
 *
 * 数据全部走 /api/assets/*，本项目上下文（useProject）提供 project_id。
 */

import { useState } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import {
  Button,
  Descriptions,
  Drawer,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Segmented,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { callApi, ApiError } from "../lib/api";
import { useProject } from "../lib/project";
import { AsyncBoundary } from "../components/AsyncBoundary";

// ---------------------------------------------------------------------------
// 类型注册（与后端 modules/assets.ts 的 assetDefs 对应）
// ---------------------------------------------------------------------------

interface AssetRow {
  id: string;
  name: string;
  version: number;
  status: string;
  created_at: string;
  updated_at: string;
  [k: string]: unknown;
}

interface AssetListData {
  items: AssetRow[];
  total: number | null;
  page: number;
  page_size: number;
}

const STATUS_COLOR: Record<string, string> = {
  draft: "default",
  processing: "processing",
  ready: "success",
  rejected: "error",
  archived: "warning",
};

const STATUS_LABEL: Record<string, string> = {
  draft: "草稿",
  processing: "处理中",
  ready: "就绪",
  rejected: "已驳回",
  archived: "已归档",
};

const ALGO_LABEL: Record<string, string> = {
  vla: "VLA",
  il: "模仿学习",
  rl: "强化学习",
  scripted: "脚本策略",
  hybrid: "混合",
};

interface AssetTypeDef {
  key: string;
  label: string;
  /** 列表额外列 */
  columns: ColumnsType<AssetRow>;
  /** 创建表单字段（name 之外） */
  createFields: React.ReactNode;
  /** 创建请求体的组装（从表单值 → API body） */
  buildCreateBody: (v: Record<string, unknown>) => Record<string, unknown>;
}

const ASSET_TYPES: AssetTypeDef[] = [
  {
    key: "env",
    label: "环境",
    columns: [
      { title: "来源", dataIndex: "source_type", width: 110 },
      { title: "尺度单位", dataIndex: "scale_unit", width: 100 },
    ],
    createFields: (
      <>
        <Form.Item
          name="source_type"
          label="来源类型"
          rules={[{ required: true, message: "选择来源类型" }]}
        >
          <Select
            options={[
              { value: "gaussian", label: "3DGS（gaussian）" },
              { value: "mesh", label: "Mesh" },
              { value: "usd", label: "USD" },
              { value: "mjcf", label: "MJCF" },
            ]}
          />
        </Form.Item>
        <Form.Item name="scale_unit" label="尺度单位" initialValue="meter">
          <Select
            options={[
              { value: "meter", label: "米（meter）" },
              { value: "centimeter", label: "厘米（centimeter）" },
            ]}
          />
        </Form.Item>
      </>
    ),
    buildCreateBody: (v) => v,
  },
  {
    key: "robot",
    label: "本体",
    columns: [
      { title: "自由度", dataIndex: "dof", width: 90 },
      { title: "自重(kg)", dataIndex: "mass_kg", width: 100 },
      { title: "负载(kg)", dataIndex: "payload_kg", width: 100 },
    ],
    createFields: (
      <>
        <Form.Item name="dof" label="自由度">
          <InputNumber min={0} max={500} style={{ width: "100%" }} />
        </Form.Item>
        <Form.Item name="mass_kg" label="自重(kg)">
          <InputNumber min={0} style={{ width: "100%" }} />
        </Form.Item>
      </>
    ),
    buildCreateBody: (v) => v,
  },
  {
    key: "task",
    label: "任务",
    columns: [
      {
        title: "指令",
        dataIndex: "instruction",
        ellipsis: true,
      },
      { title: "最大步数", dataIndex: "max_steps", width: 100 },
    ],
    createFields: (
      <>
        <Form.Item
          name="instruction"
          label="任务指令"
          rules={[{ required: true, message: "填写自然语言任务指令" }]}
        >
          <Input.TextArea rows={2} placeholder="例：把桌面上的红色方块放进抽屉" />
        </Form.Item>
        <Form.Item name="max_steps" label="最大步数" initialValue={500}>
          <InputNumber min={1} max={100000} style={{ width: "100%" }} />
        </Form.Item>
      </>
    ),
    buildCreateBody: (v) => v,
  },
  {
    key: "policy",
    label: "策略",
    columns: [
      {
        title: "算法",
        dataIndex: "algo",
        width: 120,
        render: (v: string) => ALGO_LABEL[v] ?? v,
      },
      { title: "基座模型", dataIndex: "base_model", ellipsis: true },
    ],
    createFields: (
      <>
        <Form.Item name="algo" label="算法" rules={[{ required: true, message: "选择算法类别" }]}>
          <Select
            options={Object.entries(ALGO_LABEL).map(([value, label]) => ({ value, label }))}
          />
        </Form.Item>
        <Form.Item name="base_model" label="基座模型">
          <Input placeholder="例：openvla-7b（scripted 可留空）" />
        </Form.Item>
      </>
    ),
    buildCreateBody: (v) => {
      const body = { ...v };
      if (!body.base_model) delete body.base_model;
      return body;
    },
  },
];

function fmtTime(s?: string): string {
  return s ? new Date(s).toLocaleString("zh-CN", { hour12: false }) : "-";
}

// ---------------------------------------------------------------------------
// 创建弹窗
// ---------------------------------------------------------------------------

function CreateAssetModal({
  type,
  open,
  onClose,
  projectId,
}: {
  type: AssetTypeDef;
  open: boolean;
  onClose: () => void;
  projectId: string;
}) {
  const qc = useQueryClient();
  const [form] = Form.useForm();

  const m = useMutation({
    mutationFn: async (values: Record<string, unknown>) => {
      const body = { project_id: projectId, name: values.name, ...type.buildCreateBody(values) };
      return (await callApi(`/api/assets/${type.key}`, { method: "POST", body })).data;
    },
    onSuccess: () => {
      message.success(`${type.label}资产已创建（版本 1，创建即冻结）`);
      void qc.invalidateQueries({ queryKey: ["assets"] });
      onClose();
    },
    onError: (e) => message.error(e instanceof ApiError ? `${e.message}（code=${e.code}）` : String(e)),
  });

  return (
    <Modal
      title={`新建${type.label}资产`}
      open={open}
      onCancel={onClose}
      onOk={() => form.submit()}
      confirmLoading={m.isPending}
      destroyOnHidden
    >
      <Form form={form} layout="vertical" onFinish={(v) => m.mutate(v)}>
        <Form.Item name="name" label="名称" rules={[{ required: true, message: "填写名称" }]}>
          <Input placeholder="同项目下同名即新版本，当前固定 v1" />
        </Form.Item>
        {type.createFields}
      </Form>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// 详情抽屉（元信息 + 引用关系）
// ---------------------------------------------------------------------------

function AssetDetailDrawer({
  type,
  assetId,
  onClose,
}: {
  type: AssetTypeDef;
  assetId: string | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const detail = useQuery({
    queryKey: ["asset", type.key, assetId],
    queryFn: async () => (await callApi<AssetRow>(`/api/assets/${type.key}/${assetId}`)).data,
    enabled: !!assetId,
  });
  const refs = useQuery({
    queryKey: ["assetRefs", type.key, assetId],
    queryFn: async () =>
      (
        await callApi<{ count: number; recent: { id: string; name: string; status: string }[] }>(
          `/api/assets/${type.key}/${assetId}/refs`
        )
      ).data,
    enabled: !!assetId,
  });

  const archive = useMutation({
    mutationFn: async () =>
      (await callApi(`/api/assets/${type.key}/${assetId}/archive`, { method: "POST" })).data,
    onSuccess: () => {
      message.success("已归档");
      void qc.invalidateQueries({ queryKey: ["assets"] });
      void qc.invalidateQueries({ queryKey: ["asset", type.key, assetId] });
    },
    onError: (e) => message.error(e instanceof ApiError ? e.message : String(e)),
  });

  const row = detail.data;
  type Meta = [string, React.ReactNode];
  const meta: Meta[] = row
    ? ([
        ["名称", row.name],
        ["版本", `v${row.version}`],
        ["状态", <Tag key="s" color={STATUS_COLOR[row.status]}>{STATUS_LABEL[row.status] ?? row.status}</Tag>],
        ["创建时间", fmtTime(row.created_at)],
        ["更新时间", fmtTime(row.updated_at)],
        ...(type.key === "env"
          ? ([
              ["来源类型", String(row.source_type ?? "-")],
              ["尺度单位", String(row.scale_unit ?? "-")],
            ] as Meta[])
          : []),
        ...(type.key === "robot"
          ? ([
              ["自由度", String(row.dof ?? "-")],
              ["自重(kg)", String(row.mass_kg ?? "-")],
            ] as Meta[])
          : []),
        ...(type.key === "task"
          ? ([
              ["指令", String(row.instruction ?? "-")],
              ["最大步数", String(row.max_steps ?? "-")],
            ] as Meta[])
          : []),
        ...(type.key === "policy"
          ? ([
              ["算法", ALGO_LABEL[String(row.algo)] ?? String(row.algo ?? "-")],
              ["基座模型", String(row.base_model ?? "-")],
            ] as Meta[])
          : []),
      ] as Meta[])
    : [];

  return (
    <Drawer title={`${type.label}资产详情`} open={!!assetId} onClose={onClose} width={520}>
      <AsyncBoundary loading={detail.isLoading} error={detail.error} onRetry={() => detail.refetch()}>
        {row && (
          <>
            <Descriptions
              column={1}
              size="small"
              bordered
              items={meta.map(([k, v]) => ({ key: k, label: k, children: v as React.ReactNode }))}
            />
            <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
              {row.status !== "archived" && (
                <Popconfirm title="归档后不可再被新实验引用，确认？" onConfirm={() => archive.mutate()}>
                  <Button danger loading={archive.isPending}>
                    归档
                  </Button>
                </Popconfirm>
              )}
            </div>

            <Typography.Title level={5} style={{ marginTop: 24 }}>
              引用关系
            </Typography.Title>
            <AsyncBoundary loading={refs.isLoading} error={refs.error} onRetry={() => refs.refetch()}>
              {refs.data && (
                <>
                  <Typography.Paragraph>
                    被引用次数：<b>{refs.data.count}</b>
                    <Typography.Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                      （统计 experiment 表中引用本资产的记录）
                    </Typography.Text>
                  </Typography.Paragraph>
                  {refs.data.recent.length > 0 && (
                    <ul style={{ paddingLeft: 18 }}>
                      {refs.data.recent.map((r) => (
                        <li key={r.id}>
                          {r.name} <Tag style={{ marginLeft: 4 }}>{STATUS_LABEL[r.status] ?? r.status}</Tag>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}
            </AsyncBoundary>
          </>
        )}
      </AsyncBoundary>
    </Drawer>
  );
}

// ---------------------------------------------------------------------------
// 主页面
// ---------------------------------------------------------------------------

export function AssetsPage() {
  const { projectId, projectLoading, projects } = useProject();
  const [typeKey, setTypeKey] = useState<string>("env");
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<string | undefined>();
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);
  const qc = useQueryClient();

  const type = ASSET_TYPES.find((t) => t.key === typeKey) ?? ASSET_TYPES[0]!;

  const list = useQuery({
    queryKey: ["assets", projectId, typeKey, q, status ?? "", page],
    queryFn: async () => {
      const p = new URLSearchParams({
        project_id: projectId ?? "",
        page: String(page),
        page_size: "10",
      });
      if (q) p.set("q", q);
      if (status) p.set("status", status);
      return (await callApi<AssetListData>(`/api/assets/${type.key}?${p.toString()}`)).data;
    },
    enabled: !!projectId,
    placeholderData: (prev) => prev,
  });

  if (projectLoading) {
    return (
      <div style={{ padding: 48, textAlign: "center" }}>
        <Typography.Text type="secondary">项目加载中……</Typography.Text>
      </div>
    );
  }
  if (!projectId) {
    return (
      <Empty description="还没有项目——请先在「项目」页创建一个项目" style={{ marginTop: 64 }} />
    );
  }

  const projectName = projects.find((p) => p.id === projectId)?.name ?? "";

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <Space>
          <Typography.Title level={4} style={{ margin: 0 }}>
            资产中心
          </Typography.Title>
          <Typography.Text type="secondary">{projectName}</Typography.Text>
        </Space>
        <Space wrap>
          <Segmented
            options={ASSET_TYPES.map((t) => ({ label: t.label, value: t.key }))}
            value={typeKey}
            onChange={(v) => {
              setTypeKey(v as string);
              setPage(1);
              setQ("");
              setStatus(undefined);
            }}
          />
          <Input.Search
            allowClear
            placeholder="按名称搜索"
            style={{ width: 220 }}
            onSearch={(v) => {
              setQ(v.trim());
              setPage(1);
            }}
          />
          <Select
            allowClear
            placeholder="状态"
            style={{ width: 120 }}
            value={status}
            onChange={(v) => {
              setStatus(v);
              setPage(1);
            }}
            options={["draft", "processing", "ready", "rejected", "archived"].map((s) => ({
              value: s,
              label: STATUS_LABEL[s],
            }))}
          />
          <Button type="primary" onClick={() => setCreateOpen(true)}>
            新建{type.label}
          </Button>
        </Space>
      </div>

      <Table<AssetRow>
        rowKey="id"
        size="middle"
        loading={list.isLoading}
        columns={[
          {
            title: "名称",
            dataIndex: "name",
            render: (v: string, r) => (
              <a onClick={() => setDetailId(r.id)}>
                {v} <Typography.Text type="secondary">v{r.version}</Typography.Text>
              </a>
            ),
          },
          ...type.columns,
          {
            title: "状态",
            dataIndex: "status",
            width: 100,
            render: (v: string) => <Tag color={STATUS_COLOR[v]}>{STATUS_LABEL[v] ?? v}</Tag>,
          },
          { title: "创建时间", dataIndex: "created_at", width: 170, render: fmtTime },
        ]}
        dataSource={list.data?.items ?? []}
        pagination={{
          current: page,
          pageSize: 10,
          total: list.data?.total ?? 0,
          showSizeChanger: false,
          showTotal: (t) => `共 ${t} 条`,
          onChange: (p) => setPage(p),
        }}
        locale={{ emptyText: <Empty description={`暂无${type.label}资产`} /> }}
        onRow={(r) => ({ onClick: () => setDetailId(r.id), style: { cursor: "pointer" } })}
      />

      <CreateAssetModal
        type={type}
        open={createOpen}
        onClose={() => {
          setCreateOpen(false);
          qc.invalidateQueries({ queryKey: ["assets"] });
        }}
        projectId={projectId}
      />
      <AssetDetailDrawer type={type} assetId={detailId} onClose={() => setDetailId(null)} />
    </div>
  );
}
